import React, { FC } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Skeleton,
  Typography,
  Alert,
  TextField,
  InputAdornment,
  Grid,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import dayjs from 'dayjs';
import Dinero from 'views/rebranded-module/common/utils/Dinero';
import moneyFormat from 'views/rebranded-module/common/utils/moneyFormat';
import Typography500 from 'views/common/components/UI/Typography500/Typography500';
import { FormErrors } from 'views/common/components';
import { TourPackageRadio } from 'views/rebranded-module/tours-module/common/components/TourBookingWidget/components/TourPackageRadio';
import {
  WIDGET_KEYS,
  WIDGET_TYPE_IDENTIFIER,
} from 'views/rebranded-module/deals-module/DealDetailsViewV2/utils/constants';
import { CounterV2 } from 'views/rebranded-module/common/components/UI/CounterV2';
import { useContent } from 'views/rebranded-module/common/context/useContent';
import StyledBox, {
  StyledDatePicker,
  StyledTooltip,
  StyledMenuPaper,
} from 'views/rebranded-module/deals-module/DealDetailsViewV2/components/TourPackageWidget/TourPackageWidget.styles';
import { interpolateString } from 'views/rebranded-module/common/utils/interpolateString';
import {
  RiCalendar2Fill,
  RiMapPin2Line,
} from 'react-icons/ri';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { DATE_FORMAT_US } from 'views/rebranded-module/common/utils/dateFormate';
import { useTheme } from '@mui/material/styles';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
interface TourPackageWidgetProps {
  dealTours: any;
  deal: any;
  formData: any;
  widgetData?: any;
  handleFormDataChange: (key: string, value: any) => void;
  handleShouldDisableDate: (date: unknown) => boolean;
  getOccurrenceName: (o: unknown, selected?: boolean) => any;
  handleBookTour: () => void;
}

const TourPackageWidget: FC<TourPackageWidgetProps> = (props) => {
  const {
    dealTours,
    deal,
    formData,
    widgetData,
    handleFormDataChange,
    handleShouldDisableDate,
    getOccurrenceName,
    handleBookTour,
  } = props;
  const theme = useTheme();
  const content = useContent('TourBookingWidgetComponent');
  return (
    <StyledBox>
      <Box mb={2}>
        <TextField
          value={formData.tour_id}
          id="tour_date"
          name="tour_date"
          variant="filled"
          className="TBWPackageSelector"
          label={content.pickTourLable}
          onChange={(e) =>
            handleFormDataChange(WIDGET_KEYS.TOUR_ID, e.target.value)
          }
          fullWidth
          select
          disabled={deal.disabled}
          SelectProps={{
            IconComponent: () => null,
            MenuProps: {
              disableScrollLock: true,
              PaperProps: {
                component: StyledMenuPaper,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                className="TBWPackageSelectorIcon">
                <RiMapPin2Line />
              </InputAdornment>
            ),
          }}>
          {dealTours.tours.map((tour) => (
            <MenuItem className="TBWPackageItem" value={tour.id} key={tour.id}>
              {`${tour.name} (${tour.type_name})`}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box my={2}>
        {widgetData?.tour?.type_identifier ===
          WIDGET_TYPE_IDENTIFIER.SELF_DRIVE ||
          widgetData?.tour?.type_identifier ===
          WIDGET_TYPE_IDENTIFIER.GUIDED_RECURRING ? (
          <>
            {widgetData?.calendar_options && (
              <StyledDatePicker
                className="customDatePicker"
                required
                fullWidth
                disabled={deal.disabled}
                variant="filled"
                color="secondary"
                format={DATE_FORMAT_US}
                minDate={dayjs(widgetData.calendar_options.min_date)}
                maxDate={dayjs(widgetData.calendar_options.max_date)}
                id="tour_start_date"
                label={content.tourStartDateLabel}
                placeholder="mm/dd/yyyy"
                value={dayjs(
                  formData.pickup_date || widgetData.calendar_options.min_date,
                )}
                onChange={(date) =>
                  handleFormDataChange(WIDGET_KEYS.PICKUP_DATE, date)
                }
                shouldDisableDate={handleShouldDisableDate}
                views={['day']}
                disableCloseOnSelect={false}
                slotProps={{
                  textField: {
                    className: 'TBWPackageSelector',
                    variant: 'filled',
                    InputProps: {
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          className="TBWPackageSelectorIcon">
                          <RiCalendar2Fill />
                        </InputAdornment>
                      ),
                      endAdornment: null,
                    },
                  },
                  // For mobile (dialog)
                  dialog: {
                    sx: {
                      '& .MuiDialog-paper': {
                        width: '90vw',
                        maxWidth: '90vw',
                        margin: 2,
                        padding:1.5,
                      },
                      '& .MuiPickersToolbar-root ':{
                        padding: theme.spacing(1, 0.5),
                      },
                    },
                  },
                }}
              />
            )}
            {formData.errors && (
              <FormErrors
                errorMsgs={formData.errors}
                errorField="pickup_date"
              />
            )}
          </>
        ) : (
          <>
            {widgetData?.occurrences?.length > 0 && (
              <TextField
                value={formData.occurrence_id || ''}
                id="tour_date"
                name="tour_date"
                variant="filled"
                className="TBWPackageSelector"
                label={content.tourStartDateLabel}
                onChange={(e) =>
                  handleFormDataChange(
                    WIDGET_KEYS.OCCURRENCE_ID,
                    e.target.value,
                  )
                }
                fullWidth
                select
                disabled={deal.disabled}
                SelectProps={{
                  renderValue: (value) => {
                    const occurrence = widgetData?.occurrences?.find((o: any) => o.id === value);
                    if (!occurrence) return null;
                    return getOccurrenceName(occurrence, true);
                  },
                  IconComponent: () => null,
                  MenuProps: {
                    disableScrollLock: true,
                    PaperProps: {
                      component: StyledMenuPaper,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      className="TBWPackageSelectorIcon">
                      <RiCalendar2Fill />
                    </InputAdornment>
                  ),
                }}>
                {widgetData.occurrences.map((occurrence) => (
                  <MenuItem
                    className="TBWPackageItem"
                    value={occurrence.id}
                    key={occurrence.id}>
                    {getOccurrenceName(occurrence)}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {formData.errors?.errors?.occurrence_id?.length > 0 && (
              <Alert
                className="TBWError"
                severity="error"
                icon={<ErrorIcon className="TBWErrorIcon" />}>
                {formData.errors.errors.occurrence_id.join(', ')}
              </Alert>
            )}
          </>
        )}
        {!widgetData && <Skeleton variant="text" height={70} />}
      </Box>
      <Box my={2} id="select_tour_package">
        <Box mt={2}>
          {widgetData ? (
            <>
              {widgetData?.packages.map((pack, index) => (
                <Box mb={2} key={pack.id}>
                  <TourPackageRadio
                    index={index}
                    offRoadPackages={
                      widgetData.tour.type_identifier ===
                      WIDGET_TYPE_IDENTIFIER.GUIDED_RECURRING
                    }
                    packageName={
                      widgetData.tour.type_identifier ===
                        WIDGET_TYPE_IDENTIFIER.GUIDED_RECURRING
                        ? interpolateString(content.seaterLabel, {
                          seater_number: pack.people_count,
                          count: pack.people_count === 2 ? 1 : 3,
                        })
                        : null
                    }
                    id={pack.id}
                    key={pack.id}
                    adultsNumber={pack.people_count}
                    numberOfMoto={pack.motorcycles_count}
                    numberOfBed={pack.beds_count}
                    pricePerPerson={moneyFormat(
                      Dinero({
                        amount: pack.price_per_person.amount,
                        currency: pack.price_per_person.currency_code,
                      }),
                    )}
                    active={pack.id == formData.package_id && !deal.disabled}
                    onClick={(id) =>
                      handleFormDataChange(WIDGET_KEYS.PACKAGE_ID, id)
                    }
                    disabled={deal.disabled}
                    showPriceOnly={
                      widgetData.tour.type_identifier ===
                      WIDGET_TYPE_IDENTIFIER.GUIDED_RECURRING
                    }
                    isMultiDayOffroad={pack.is_multi_day_offroad}
                    minPersons={pack.min_persons}
                    maxPersons={pack.max_persons}
                  />
                </Box>
              ))}
              {formData.errors?.errors?.package_id?.length > 0 && (
                <Alert
                  className="TBWError"
                  severity="error"
                  icon={<ErrorIcon className="TBWErrorIcon" />}>
                  {formData.errors.errors.package_id.join(', ')}
                </Alert>
              )}
            </>
          ) : (
            <>
              <Skeleton variant="text" height={80} />
              <Skeleton variant="text" height={80} />
            </>
          )}
        </Box>
      </Box>

      {widgetData?.recurring_tour_availabilities?.length > 0 && (
        <Box my={2} id="time_slots">
          <Typography500 variant="body2" mb={2}>
            {content.timeSlotsText}
          </Typography500>
          <Box className="TPSTimeSlotsWrapper">
            <Grid container spacing={1}>
              {widgetData.recurring_tour_availabilities.map((item) => (
                <Grid item xs={4} key={item}>
                  <Box
                    className={`TPSTimeSlots ${
                      item.id === formData.availability_id ? 'active' : ''
                      }`}
                    onClick={() =>
                      handleFormDataChange(WIDGET_KEYS.AVAILABILITY_ID, item.id)
                    }>
                    <Typography className="TPSTimeSlotsText" variant="body2">
                      {item.time_part}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      )}
      {formData?.errors?.errors?.availability?.length > 0 && (
        <Alert
          className="TPSTimeSlotsError"
          severity="error"
          icon={<ErrorIcon className="TPSTimeSlotsErrorIcon" />}>
          {formData.errors.errors.availability.join(', ')}
        </Alert>
      )}
      <Box my={2}>
        <CounterV2
          count={formData.packages_count}
          min={1}
          max={10}
          onIncrement={() =>
            handleFormDataChange(
              WIDGET_KEYS.PACKAGES_COUNT,
              formData.packages_count + 1,
            )
          }
          onDecrement={() =>
            handleFormDataChange(
              WIDGET_KEYS.PACKAGES_COUNT,
              formData.packages_count - 1,
            )
          }
          inactive={deal.disabled}
          title={
            widgetData?.tour?.type_identifier ===
              WIDGET_TYPE_IDENTIFIER.GUIDED_RECURRING
              ? content.offroadNoOfPackagesText
              : content.numberOfPackagesText
          }
        />
        {formData.errors?.errors?.packages_count?.length > 0 && (
          <Alert
            className="TBWError"
            severity="error"
            icon={<ErrorIcon className="TBWErrorIcon" />}>
            {formData.errors.errors.packages_count.join(', ')}
          </Alert>
        )}
      </Box>

      <Box my={2}>
        <Button
          fullWidth
          variant="contained"
          disableElevation
          onClick={handleBookTour}
          disabled={deal.disabled}
          color="primary">
          {content.bookBtnText}
        </Button>
      </Box>
    </StyledBox>
  );
};

export { TourPackageWidget };
